import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { SalesforcePromotionCountdown } from '~/lib/salesforce/types';
import { getPromotionCountdownEndTime } from '~/shared/utils/get-promotion-countdown-end-time';
import { trustedTime } from '~/shared/utils/trusted-time';

// `setTimeout` fires right away if asked to wait more than ~24 days, so long waits are split.
const TIMER_SLICE_MS = 7 * 24 * 60 * 60 * 1000;

/** The earliest end time after `now`. Without `now`, the earliest of them all. */
const getNextEndTime = (endTimes: number[], now = -Infinity): number | null =>
    endTimes.reduce<number | null>(
        (next, endTime) => (endTime > now && (next === null || endTime < next) ? endTime : next),
        null,
    );

/**
 * The promotion whose countdown ends next, or `undefined` once none is left running.
 *
 * Timed against `countdownEndDate` rather than `isCountdownActive`, because the response that
 * carried it may be cached and outlive the countdown. Salesforce alone decides when the discount
 * applies; `onEnd` is where a caller fetches the data that reflects it.
 */
export const usePromotionCountdown = (
    promotions?: SalesforcePromotionCountdown[],
    onEnd?: () => void,
): SalesforcePromotionCountdown | undefined => {
    // A string, so a refetch carrying the same countdowns keeps the subscription.
    const endTimesKey = (promotions ?? [])
        .map(getPromotionCountdownEndTime)
        .filter((endTime): endTime is number => endTime !== null)
        .join(',');

    const endTimes = useMemo(
        () => (endTimesKey ? endTimesKey.split(',').map(Number) : []),
        [endTimesKey],
    );

    // Wakes React each time a countdown reaches zero, so the next one can take over.
    const subscribe = useCallback(
        (onChange: () => void) => {
            let timer: ReturnType<typeof setTimeout> | undefined;

            const startTimer = () => {
                const now = trustedTime.now();
                const nextEndTime = getNextEndTime(endTimes, now);

                if (nextEndTime === null) return;

                timer = setTimeout(
                    () => {
                        onChange();
                        startTimer();
                    },
                    Math.min(nextEndTime - now, TIMER_SLICE_MS),
                );
            };

            // A reset clock leaves our timer aimed at the wrong moment. Start it over.
            const restartTimer = () => {
                clearTimeout(timer);
                startTimer();
                onChange();
            };

            startTimer();

            const unsubscribe = trustedTime.subscribe(restartTimer);

            return () => {
                clearTimeout(timer);
                unsubscribe();
            };
        },
        [endTimes],
    );

    const readNextEndTime = useCallback(
        () => getNextEndTime(endTimes, trustedTime.now()),
        [endTimes],
    );

    // Taken from the data alone, so the server and the hydrating client agree. The client then
    // drops whatever a cached page still counts down to.
    const readNextEndTimeOnServer = useCallback(() => getNextEndTime(endTimes), [endTimes]);

    const nextEndTime = useSyncExternalStore(subscribe, readNextEndTime, readNextEndTimeOnServer);

    const previousEndTimeRef = useRef(nextEndTime);

    // Rerunning for a new `onEnd` alone is harmless: the end time it compares has not moved.
    useEffect(() => {
        const previousEndTime = previousEndTimeRef.current;
        previousEndTimeRef.current = nextEndTime;

        // Only a countdown that reached zero ends here; switching to other data is not an end.
        if (
            previousEndTime !== null &&
            previousEndTime !== nextEndTime &&
            previousEndTime <= trustedTime.now()
        ) {
            onEnd?.();
        }
    }, [nextEndTime, onEnd]);

    if (nextEndTime === null) return undefined;

    return promotions?.find((promotion) => getPromotionCountdownEndTime(promotion) === nextEndTime);
};
